import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/adminApi";
import { DEFAULT_CONTENT, mergeContent } from "@/lib/defaultContent";
import {
  Save,
  RotateCcw,
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  Image as ImageIcon,
} from "lucide-react";

export default function ContentEditor() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [openSection, setOpenSection] = useState("hero");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/site-content");
      setContent(mergeContent(DEFAULT_CONTENT, data?.content || {}));
    } catch {
      toast.error("Failed to load site content");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site-content", { content });
      toast.success("Site content saved. Landing page will refresh.");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const resetAll = async () => {
    if (!window.confirm("Reset ALL page content back to defaults? This cannot be undone.")) return;
    try {
      const { data } = await api.post("/admin/site-content/reset");
      setContent(mergeContent(DEFAULT_CONTENT, data?.content || {}));
      toast.success("Content reset to defaults");
    } catch {
      toast.error("Failed to reset");
    }
  };

  if (!content) {
    return <div className="text-[#616161] py-12 text-center">Loading content…</div>;
  }

  const setSection = (key, next) => setContent((c) => ({ ...c, [key]: next }));

  const sections = [
    { key: "hero", label: "Hero / Cover" },
    { key: "who_we_are", label: "Who We Are" },
    { key: "why_dubai", label: "Why Dubai" },
    { key: "philosophy", label: "Investment Philosophy" },
    { key: "opportunities", label: "Featured Opportunities (heading)" },
    { key: "trending", label: "Most Trending Projects (heading)" },
    { key: "process", label: "Advisory Process" },
    { key: "trust", label: "Why Clients Trust Us" },
    { key: "insights", label: "Market Insights" },
    { key: "cta", label: "Final CTA / Contact Form" },
    { key: "footer", label: "Footer" },
    { key: "navbar", label: "Navigation" },
    { key: "whatsapp_float", label: "Floating WhatsApp" },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h2 className="font-serif-luxe text-3xl text-[#1D1D1F]">Page Content</h2>
          <p className="text-sm text-[#616161] mt-2">
            Edit every text, image, button, card and form option on the landing page.
          </p>
        </div>
        <div className="flex gap-3">
          <button onClick={resetAll} className="btn-outline-dark" data-testid="content-reset">
            <RotateCcw size={14} strokeWidth={1.4} /> Reset to Defaults
          </button>
          <button onClick={save} disabled={saving} className="btn-luxe" data-testid="content-save">
            <Save size={14} strokeWidth={1.4} /> {saving ? "Saving…" : "Save & Publish"}
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {sections.map((s) => (
          <Section
            key={s.key}
            label={s.label}
            open={openSection === s.key}
            onToggle={() => setOpenSection(openSection === s.key ? null : s.key)}
            testid={`content-section-${s.key}`}
          >
            {renderEditor(s.key, content[s.key], (next) => setSection(s.key, next))}
          </Section>
        ))}
      </div>

      <div className="mt-8 flex justify-end">
        <button onClick={save} disabled={saving} className="btn-luxe" data-testid="content-save-bottom">
          <Save size={14} strokeWidth={1.4} /> {saving ? "Saving…" : "Save & Publish"}
        </button>
      </div>
    </div>
  );
}

/* -------- Section accordion wrapper -------- */
function Section({ label, open, onToggle, testid, children }) {
  return (
    <div className="bg-white border border-black/10" data-testid={testid}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-6 py-4 text-left"
      >
        <span className="font-serif-luxe text-xl text-[#1D1D1F]">{label}</span>
        {open ? <ChevronDown size={18} strokeWidth={1.4} /> : <ChevronRight size={18} strokeWidth={1.4} />}
      </button>
      {open && <div className="px-6 pb-8 border-t border-black/10 pt-6">{children}</div>}
    </div>
  );
}

/* -------- Generic field helpers -------- */
function TextField({ label, value, onChange, textarea, placeholder, span = 1, testid }) {
  return (
    <div className={span === 2 ? "md:col-span-2" : ""}>
      <label className="text-[0.7rem] tracking-[0.16em] uppercase text-[#616161]">{label}</label>
      {textarea ? (
        <textarea
          rows={3}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="input-luxe resize-none"
          placeholder={placeholder}
          data-testid={testid}
        />
      ) : (
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="input-luxe"
          placeholder={placeholder}
          data-testid={testid}
        />
      )}
    </div>
  );
}

function ImageField({ label, value, onChange, testid }) {
  return (
    <div className="md:col-span-2">
      <label className="text-[0.7rem] tracking-[0.16em] uppercase text-[#616161] flex items-center gap-2">
        <ImageIcon size={12} strokeWidth={1.4} /> {label}
      </label>
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="input-luxe"
        placeholder="https://…"
        data-testid={testid}
      />
      {value && (
        <img src={value} alt="preview" className="w-full h-40 object-cover mt-3" onError={(e) => { e.currentTarget.style.opacity = "0.4"; }} />
      )}
    </div>
  );
}

/* -------- Array editor -------- */
function ArrayEditor({ title, items, template, fields, onChange, min = 0, testidPrefix }) {
  const update = (i, patch) => {
    const next = items.slice();
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const remove = (i) => {
    if (items.length <= min) return;
    onChange(items.filter((_, idx) => idx !== i));
  };
  const add = () => onChange([...items, { ...template }]);

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-medium text-[#1D1D1F]">{title} ({items.length})</div>
        <button type="button" onClick={add} className="text-xs tracking-[0.14em] uppercase link-gold inline-flex items-center gap-1" data-testid={`${testidPrefix}-add`}>
          <Plus size={12} strokeWidth={1.4} /> Add
        </button>
      </div>
      <div className="space-y-4">
        {items.map((it, i) => (
          <div key={i} className="border border-black/10 p-4 bg-[#FAFAF8] relative" data-testid={`${testidPrefix}-item-${i}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="text-[0.65rem] tracking-[0.16em] uppercase text-[#616161]">#{i + 1}</div>
              <button type="button" onClick={() => remove(i)} disabled={items.length <= min} className="text-red-600 hover:text-red-800 disabled:opacity-30" data-testid={`${testidPrefix}-remove-${i}`}>
                <Trash2 size={14} strokeWidth={1.4} />
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-x-5 gap-y-4">
              {fields.map((f) => (
                f.type === "image" ? (
                  <ImageField
                    key={f.key}
                    label={f.label}
                    value={it[f.key]}
                    onChange={(v) => update(i, { [f.key]: v })}
                    testid={`${testidPrefix}-${i}-${f.key}`}
                  />
                ) : (
                  <TextField
                    key={f.key}
                    label={f.label}
                    value={it[f.key]}
                    onChange={(v) => update(i, { [f.key]: v })}
                    textarea={f.type === "textarea"}
                    placeholder={f.placeholder}
                    span={f.span || (f.type === "textarea" ? 2 : 1)}
                    testid={`${testidPrefix}-${i}-${f.key}`}
                  />
                )
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------- String-array editor (budgets, objectives, consultations) -------- */
function StringArrayEditor({ title, items, onChange, testidPrefix, placeholder }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[0.7rem] tracking-[0.16em] uppercase text-[#616161]">{title} ({items.length})</div>
        <button type="button" onClick={() => onChange([...items, ""])} className="text-xs tracking-[0.14em] uppercase link-gold inline-flex items-center gap-1" data-testid={`${testidPrefix}-add`}>
          <Plus size={12} strokeWidth={1.4} /> Add
        </button>
      </div>
      <div className="space-y-2">
        {items.map((v, i) => (
          <div key={i} className="flex gap-2 items-center">
            <input
              value={v}
              onChange={(e) => {
                const next = items.slice(); next[i] = e.target.value; onChange(next);
              }}
              className="input-luxe flex-1"
              placeholder={placeholder}
              data-testid={`${testidPrefix}-${i}`}
            />
            <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="text-red-600 hover:text-red-800" data-testid={`${testidPrefix}-remove-${i}`}>
              <Trash2 size={14} strokeWidth={1.4} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------- Per-section editors -------- */
function renderEditor(key, data, onChange) {
  const set = (patch) => onChange({ ...data, ...patch });
  switch (key) {
    case "hero":
      return (
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
          <TextField label="Eyebrow" value={data.eyebrow} onChange={(v) => set({ eyebrow: v })} testid="hero-eyebrow" />
          <TextField label="CTA Label" value={data.cta_label} onChange={(v) => set({ cta_label: v })} testid="hero-cta-label" />
          <TextField label="Heading (line 1)" value={data.heading_line1} onChange={(v) => set({ heading_line1: v })} span={2} testid="hero-heading-line1" />
          <TextField label="Heading (gold accent)" value={data.heading_accent} onChange={(v) => set({ heading_accent: v })} span={2} testid="hero-heading-accent" />
          <TextField label="Subheading" value={data.subheading} onChange={(v) => set({ subheading: v })} textarea span={2} testid="hero-subheading" />
          <TextField label="CTA Link (e.g. #contact)" value={data.cta_href} onChange={(v) => set({ cta_href: v })} testid="hero-cta-href" />
          <div />
          <ImageField label="Cover / Background Image URL" value={data.background_image} onChange={(v) => set({ background_image: v })} testid="hero-bg" />
          <div className="md:col-span-2">
            <ArrayEditor
              title="Trust Stats"
              items={data.trust_stats || []}
              onChange={(v) => set({ trust_stats: v })}
              template={{ k: "", v: "" }}
              fields={[
                { key: "k", label: "Value (e.g. 15+)" },
                { key: "v", label: "Label" },
              ]}
              testidPrefix="hero-stats"
            />
          </div>
        </div>
      );

    case "who_we_are":
      return (
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
          <TextField label="Eyebrow" value={data.eyebrow} onChange={(v) => set({ eyebrow: v })} />
          <div />
          <TextField label="Heading Line 1" value={data.heading_line1} onChange={(v) => set({ heading_line1: v })} span={2} />
          <TextField label="Heading Accent (gold, italic)" value={data.heading_accent} onChange={(v) => set({ heading_accent: v })} span={2} />
          <ImageField label="Side Image URL" value={data.image} onChange={(v) => set({ image: v })} />
          <div className="md:col-span-2">
            <label className="text-[0.7rem] tracking-[0.16em] uppercase text-[#616161]">Body Paragraphs</label>
            <div className="space-y-2 mt-2">
              {(data.body_paragraphs || []).map((p, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <textarea
                    rows={3}
                    value={p}
                    onChange={(e) => {
                      const next = data.body_paragraphs.slice(); next[i] = e.target.value; set({ body_paragraphs: next });
                    }}
                    className="input-luxe flex-1 resize-none"
                  />
                  <button type="button" onClick={() => set({ body_paragraphs: data.body_paragraphs.filter((_, idx) => idx !== i) })} className="text-red-600 hover:text-red-800 mt-2">
                    <Trash2 size={14} strokeWidth={1.4} />
                  </button>
                </div>
              ))}
              <button type="button" onClick={() => set({ body_paragraphs: [...(data.body_paragraphs || []), ""] })} className="text-xs tracking-[0.14em] uppercase link-gold inline-flex items-center gap-1 mt-1">
                <Plus size={12} strokeWidth={1.4} /> Add paragraph
              </button>
            </div>
          </div>
          <TextField label="Italic Quote" value={data.italic_quote} onChange={(v) => set({ italic_quote: v })} textarea span={2} />
          <div className="md:col-span-2">
            <ArrayEditor
              title="Statistics"
              items={data.stats || []}
              onChange={(v) => set({ stats: v })}
              template={{ k: "", v: "" }}
              fields={[{ key: "k", label: "Value" }, { key: "v", label: "Label" }]}
              testidPrefix="who-stats"
            />
          </div>
        </div>
      );

    case "why_dubai":
    case "trust":
      return (
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
          <TextField label="Eyebrow" value={data.eyebrow} onChange={(v) => set({ eyebrow: v })} />
          <div />
          <TextField label="Heading" value={data.heading} onChange={(v) => set({ heading: v })} span={2} />
          {"intro" in data && (
            <TextField label="Intro" value={data.intro} onChange={(v) => set({ intro: v })} textarea span={2} />
          )}
          <div className="md:col-span-2">
            <ArrayEditor
              title="Cards"
              items={data.cards || []}
              onChange={(v) => set({ cards: v })}
              template={{ title: "", body: "" }}
              fields={[
                { key: "title", label: "Card Title" },
                { key: "body", label: "Card Body", type: "textarea" },
              ]}
              testidPrefix={`${key}-cards`}
            />
          </div>
        </div>
      );

    case "philosophy":
    case "process":
      return (
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
          <TextField label="Eyebrow" value={data.eyebrow} onChange={(v) => set({ eyebrow: v })} />
          <div />
          <TextField label="Heading" value={data.heading} onChange={(v) => set({ heading: v })} span={2} />
          {"intro" in data && (
            <TextField label="Intro" value={data.intro} onChange={(v) => set({ intro: v })} textarea span={2} />
          )}
          <div className="md:col-span-2">
            <ArrayEditor
              title="Steps"
              items={data.steps || []}
              onChange={(v) => set({ steps: v })}
              template={{ title: "", body: "" }}
              fields={[
                { key: "title", label: "Step Title" },
                { key: "body", label: "Step Body", type: "textarea" },
              ]}
              testidPrefix={`${key}-steps`}
            />
          </div>
          {"closing_statement" in data && (
            <TextField label="Closing Statement" value={data.closing_statement} onChange={(v) => set({ closing_statement: v })} textarea span={2} />
          )}
        </div>
      );

    case "opportunities":
      return (
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
          <TextField label="Eyebrow" value={data.eyebrow} onChange={(v) => set({ eyebrow: v })} />
          <TextField label="Card CTA Label" value={data.cta_label} onChange={(v) => set({ cta_label: v })} />
          <TextField label="Heading" value={data.heading} onChange={(v) => set({ heading: v })} span={2} />
          <TextField label="Intro" value={data.intro} onChange={(v) => set({ intro: v })} textarea span={2} />
          <div className="md:col-span-2 text-xs text-[#616161] bg-[#FAFAF8] p-4 border border-black/10">
            The property cards themselves are managed in the <b>Properties</b> tab.
          </div>
        </div>
      );

    case "trending":
      return (
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
          <TextField label="Eyebrow (optional)" value={data.eyebrow} onChange={(v) => set({ eyebrow: v })} />
          <TextField label="Card Button Label" value={data.cta_label} onChange={(v) => set({ cta_label: v })} placeholder="Enquire Now" />
          <TextField label="Heading" value={data.heading} onChange={(v) => set({ heading: v })} span={2} placeholder="Dubai's Upcoming Projects" />
          <TextField label="Intro (paragraph 1)" value={data.intro} onChange={(v) => set({ intro: v })} textarea span={2} />
          <TextField label="Sub Intro (paragraph 2)" value={data.sub_intro} onChange={(v) => set({ sub_intro: v })} textarea span={2} />
          <TextField label="'Coming Soon' overlay text" value={data.coming_soon_label} onChange={(v) => set({ coming_soon_label: v })} placeholder="COMING SOON" />
          <TextField label="'Pre-Launch' badge text" value={data.pre_launch_label} onChange={(v) => set({ pre_launch_label: v })} placeholder="Pre-Launch" />
          <div className="md:col-span-2 text-xs text-[#616161] bg-[#FAFAF8] p-4 border border-black/10">
            Card content (name, location, price, description, image, Pre-Launch flag) is managed in the <b>Trending Projects</b> tab.
          </div>
        </div>
      );

    case "insights":
      return (
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
          <TextField label="Eyebrow" value={data.eyebrow} onChange={(v) => set({ eyebrow: v })} />
          <div />
          <TextField label="Heading" value={data.heading} onChange={(v) => set({ heading: v })} span={2} />
          <TextField label="Intro" value={data.intro} onChange={(v) => set({ intro: v })} textarea span={2} />
          <div className="md:col-span-2">
            <ArrayEditor
              title="Resources"
              items={data.resources || []}
              onChange={(v) => set({ resources: v })}
              template={{ title: "", tag: "" }}
              fields={[
                { key: "title", label: "Resource Title" },
                { key: "tag", label: "Tag (e.g. Q4 · 2025)" },
              ]}
              testidPrefix="insights-resources"
            />
          </div>
        </div>
      );

    case "testimonials":
      return (
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
          <TextField label="Eyebrow" value={data.eyebrow} onChange={(v) => set({ eyebrow: v })} />
          <div />
          <TextField label="Heading" value={data.heading} onChange={(v) => set({ heading: v })} span={2} />
          <div className="md:col-span-2">
            <ArrayEditor
              title="Testimonials"
              items={data.items || []}
              onChange={(v) => set({ items: v })}
              template={{ quote: "", name: "", role: "", image: "" }}
              fields={[
                { key: "quote", label: "Quote", type: "textarea" },
                { key: "name", label: "Client Name" },
                { key: "role", label: "Role / Location" },
                { key: "image", label: "Portrait Image URL", type: "image" },
              ]}
              testidPrefix="testimonials-items"
            />
          </div>
        </div>
      );

    case "cta":
      return (
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
          <TextField label="Eyebrow" value={data.eyebrow} onChange={(v) => set({ eyebrow: v })} />
          <TextField label="Submit Button Label" value={data.submit_label} onChange={(v) => set({ submit_label: v })} />
          <TextField label="Heading" value={data.heading} onChange={(v) => set({ heading: v })} span={2} />
          <TextField label="Body" value={data.body} onChange={(v) => set({ body: v })} textarea span={2} />
          <ImageField label="Section Background Image" value={data.background_image} onChange={(v) => set({ background_image: v })} />
          <TextField label="Privacy Statement" value={data.privacy_statement} onChange={(v) => set({ privacy_statement: v })} textarea span={2} />
          <TextField label="WhatsApp Label" value={data.whatsapp_label} onChange={(v) => set({ whatsapp_label: v })} />
          <TextField label="WhatsApp URL" value={data.whatsapp_url} onChange={(v) => set({ whatsapp_url: v })} />
          <TextField label="Instagram Label" value={data.instagram_label} onChange={(v) => set({ instagram_label: v })} />
          <TextField label="Instagram URL" value={data.instagram_url} onChange={(v) => set({ instagram_url: v })} />
          <div className="md:col-span-2 border-t border-black/10 pt-6 mt-2">
            <div className="grid md:grid-cols-3 gap-6">
              <StringArrayEditor title="Budget Options" items={data.budgets || []} onChange={(v) => set({ budgets: v })} testidPrefix="cta-budgets" placeholder="AED 3M – 7M" />
              <StringArrayEditor title="Objective Options" items={data.objectives || []} onChange={(v) => set({ objectives: v })} testidPrefix="cta-objectives" placeholder="Capital Appreciation" />
              <StringArrayEditor title="Consultation Types" items={data.consultations || []} onChange={(v) => set({ consultations: v })} testidPrefix="cta-consults" placeholder="Video Call" />
            </div>
          </div>
        </div>
      );

    case "footer":
      return (
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
          <TextField label="Tagline / Description" value={data.tagline} onChange={(v) => set({ tagline: v })} textarea span={2} />
          <TextField label="WhatsApp Label" value={data.whatsapp_label} onChange={(v) => set({ whatsapp_label: v })} />
          <TextField label="WhatsApp URL" value={data.whatsapp_url} onChange={(v) => set({ whatsapp_url: v })} />
          <TextField label="Instagram Label" value={data.instagram_label} onChange={(v) => set({ instagram_label: v })} />
          <TextField label="Instagram URL" value={data.instagram_url} onChange={(v) => set({ instagram_url: v })} />
          <TextField label="Copyright Line" value={data.copyright} onChange={(v) => set({ copyright: v })} span={2} />
        </div>
      );

    case "navbar":
      return (
        <div className="grid md:grid-cols-3 gap-x-6 gap-y-5">
          <TextField label="Brand Prefix" value={data.brand_prefix} onChange={(v) => set({ brand_prefix: v })} />
          <TextField label="Brand Suffix" value={data.brand_suffix} onChange={(v) => set({ brand_suffix: v })} />
          <TextField label="Consult Button Label" value={data.cta_label} onChange={(v) => set({ cta_label: v })} />
        </div>
      );

    case "whatsapp_float":
      return (
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
          <TextField label="Floating WhatsApp URL" value={data.url} onChange={(v) => set({ url: v })} span={2} placeholder="https://wa.me/+9715…" />
        </div>
      );

    default:
      return <div className="text-[#616161] text-sm">No editor defined for this section.</div>;
  }
}
